import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Box,
  useScrollTrigger,
  Slide,
  IconButton,
  useMediaQuery,
  useTheme,
  Collapse,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Menu,
  Popover,
  CircularProgress,
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import NotificationBell from './qa/NotificationBell';
import { SHOW_QA } from '../config/featureFlags';
import { buildAuthRedirectState } from '../utils/authRedirect';
import { logout } from '../utils/auth';
import {
  buildExamOverviewPath,
  buildExamTipPath,
  buildExampleVideosPath,
  buildMockExamsPath,
  buildPracticeAllMaterialPath,
  buildPracticePath,
  buildPracticeSectionPath,
  buildTopicsBasePath,
} from '../utils/contentRoutes';
import { buildLanguageContinuationPath } from '../utils/languageRouting';
import { ACCENT_RED } from '../styles/tokens';
import { getRememberedTopicMaterialPath } from '../pages/Practice/practiceMaterials';

// The bar auto-hides on scroll-down on mobile only, where vertical space is scarce.
// On desktop it stays put, so sticky page chrome can park right below it.
//
// Whatever it does, it publishes its own geometry so nothing else has to guess it:
//   --navbar-h      current top inset (0 while the bar is slid away) — for `top:`
//   --navbar-h-max  the bar's height regardless of hidden state — for `scroll-margin-top`,
//                   since scrolling up brings the bar back over a freshly targeted anchor.
function HideOnScroll({ children, forceShow }) {
  const trigger = useScrollTrigger();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const barRef = React.useRef(null);
  const visible = isDesktop || !trigger || forceShow;

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;

    const publish = () => {
      // offsetHeight is unaffected by the Slide transform, so it is the true height
      // even mid-animation.
      const height = bar.offsetHeight;
      document.body.style.setProperty('--navbar-h-max', `${height}px`);
      document.body.style.setProperty('--navbar-h', visible ? `${height}px` : '0px');
    };

    publish();
    // ResizeObserver is missing in jsdom; a resize listener covers the same cases there.
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', publish);
      return () => window.removeEventListener('resize', publish);
    }
    const observer = new ResizeObserver(publish);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <Slide appear={false} direction="down" in={visible}>
      {React.cloneElement(children, { ref: barRef })}
    </Slide>
  );
}

const NoDropdownIcon = () => null;

const ScrollableNavContainer = ({ children }) => {
  const containerRef = React.useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const checkScroll = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setShowLeft(scrollLeft > 0);
      setShowRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [children]);

  const scroll = (direction) => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: direction === 'left' ? -200 : 200, behavior: 'smooth' });
    }
  };

  return (
    <Box
      sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', maxWidth: '72%', position: 'relative', mr: 2 }}
    >
      {showLeft && (
        <IconButton
          size="small"
          onClick={() => scroll('left')}
          sx={{
            position: 'absolute',
            left: -10,
            zIndex: 2,
            bgcolor: 'grey.100',
            boxShadow: 1,
            '&:hover': { bgcolor: 'grey.100' },
          }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      )}
      <Box
        ref={containerRef}
        onScroll={checkScroll}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          overflowX: 'auto',
          scrollbarWidth: 'none', // Firefox
          '&::-webkit-scrollbar': { display: 'none' }, // Chrome/Safari
          scrollBehavior: 'smooth',
          whiteSpace: 'nowrap',
          px: 2,
        }}
      >
        {children}
      </Box>
      {showRight && (
        <IconButton
          size="small"
          onClick={() => scroll('right')}
          sx={{
            position: 'absolute',
            right: -10,
            zIndex: 2,
            bgcolor: 'grey.100',
            boxShadow: 1,
            '&:hover': { bgcolor: 'grey.100' },
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

// Brand tint reused by every practice-menu hover/active state.
const PRACTICE_ACCENT = '#133E87';
const PRACTICE_HOVER_BG = 'rgba(19,62,135,0.07)';
const PRACTICE_ACTIVE_BG = 'rgba(19,62,135,0.12)';

const PracticeGroupLabel = ({ children, sx }) => (
  <Typography
    variant="overline"
    sx={{
      px: 1.75,
      pt: 0.25,
      pb: 0.5,
      fontSize: '0.62rem',
      fontWeight: 800,
      letterSpacing: '0.09em',
      lineHeight: 1.4,
      color: 'text.secondary',
      ...sx,
    }}
  >
    {children}
  </Typography>
);

// One row in the practice dropdown. `chip` renders the compact level pills (A1/A2/B1)
// that sit side by side; the default renders a full-width topic row.
const PracticeMenuLink = ({ to, children, mobile, onClick, active, chip = false }) => {
  const shared = {
    borderRadius: 0.75,
    fontWeight: 600,
    fontSize: '0.9rem',
    color: active ? PRACTICE_ACCENT : 'text.primary',
    bgcolor: active ? PRACTICE_ACTIVE_BG : 'transparent',
    transition: 'background-color 150ms, color 150ms',
    '&:hover': { bgcolor: PRACTICE_HOVER_BG, color: PRACTICE_ACCENT, textDecoration: 'none' },
  };

  if (mobile) {
    return (
      <NavLink
        to={to}
        onClick={onClick}
        sx={{
          ...shared,
          px: chip ? 0 : 1.5,
          py: 0.75,
          textAlign: chip ? 'center' : 'left',
          ...(chip && { flex: 1, border: '1px solid', borderColor: active ? PRACTICE_ACCENT : 'divider' }),
        }}
      >
        {children}
      </NavLink>
    );
  }

  return (
    <MenuItem
      component={Link}
      to={to}
      onClick={onClick}
      sx={{
        ...shared,
        minHeight: 34,
        mx: 0.75,
        my: '1px',
        px: chip ? 0 : 1.25,
        justifyContent: chip ? 'center' : 'flex-start',
        ...(chip && { flex: 1, mx: 0, border: '1px solid', borderColor: active ? PRACTICE_ACCENT : 'divider' }),
      }}
    >
      {children}
    </MenuItem>
  );
};

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading, isAdmin } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobilePracticeOpen, setMobilePracticeOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [practiceMenuAnchorEl, setPracticeMenuAnchorEl] = useState(null);
  const [mockExamMenuAnchorEl, setMockExamMenuAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { language, changeLanguage, languageChangePending, pendingLanguage, loading: languageLoading } = useLanguage();
  const displayedLanguage = languageChangePending && pendingLanguage ? pendingLanguage : language;
  const authDecisionPending = authLoading || languageLoading;
  const languageOptions = {
    fr: {
      label: 'French',
      flag: '/flags/fr.svg',
      ariaLabel: 'French flag',
    },
    de: {
      label: 'German',
      flag: '/flags/de.svg',
      ariaLabel: 'German flag',
    },
  };

  const handleDrawerToggle = () => {
    setMobileOpen((open) => {
      if (open) {
        setMobilePracticeOpen(false);
      }
      return !open;
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleLanguageChange = async (event) => {
    const selectedLang = event.target.value;
    const result = await changeLanguage(selectedLang);
    if (!result.success) {
      return;
    }

    const continuationPath = buildLanguageContinuationPath(location, result.language ?? selectedLang);
    if (continuationPath && continuationPath !== `${location.pathname}${location.search}${location.hash}`) {
      navigate(continuationPath, { replace: true });
    }
  };

  // Profile popup handlers
  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const openProfilePopup = Boolean(anchorEl);
  const idProfilePopup = openProfilePopup ? 'simple-popover' : undefined;
  const isVerifiedUser = Boolean(user?.emailVerified);

  const accountPath = '/account';
  // Practice now opens on the journey map rather than dropping straight into the
  // dialogue catalog, so the stored-track (?notexplicit=1) dance isn't needed here.
  const chaptersPath = buildPracticePath(language);
  const videosPath = buildExampleVideosPath();
  const examOverviewPath = buildExamOverviewPath(language);
  const examTipsPath = buildExamTipPath(language);
  const practiceLinks = {
    a1: buildPracticeSectionPath(language, 'a1', 'dialogue'),
    a2: buildPracticeSectionPath(language, 'a2', 'intro'),
    b1: buildPracticeSectionPath(language, 'b1', 'discussion'),
    dialogue: getRememberedTopicMaterialPath(language, 'dialogue'),
    discussion: getRememberedTopicMaterialPath(language, 'discussion'),
    vocabulary: buildPracticeAllMaterialPath(language, 'vocab'),
    flashcards: buildPracticeAllMaterialPath(language, 'flashcards'),
  };
  // Listening by level opens Set 1 for now; the listening page handles access/path locks.
  const listeningLevelLinks = {
    a1: `${buildPracticePath(language)}/listening/a1/set1`,
    a2: `${buildPracticePath(language)}/listening/a2/set1`,
    b1: `${buildPracticePath(language)}/listening/b1/set1`,
  };
  const practiceMenuOpen = Boolean(practiceMenuAnchorEl);
  const mockExamMenuOpen = Boolean(mockExamMenuAnchorEl);
  const oralMockExamPath = buildMockExamsPath(language, 'oral', { explicit: true });
  const listeningMockExamPath = buildMockExamsPath(language, 'listening');

  const closePracticeMenu = () => setPracticeMenuAnchorEl(null);
  const closeMockExamMenu = () => setMockExamMenuAnchorEl(null);
  const closeMobileMenu = () => {
    setMobileOpen(false);
    setMobilePracticeOpen(false);
  };

  // Highlights the row the user is already on, ignoring query/hash noise.
  const isActivePath = (path) => Boolean(path) && location.pathname === path.split('?')[0];

  const PracticeLinkGroups = ({ mobile = false }) => {
    const close = mobile ? closeMobileMenu : closePracticeMenu;
    const topicLinks = [
      ['Dialogue', practiceLinks.dialogue],
      ['Discussion', practiceLinks.discussion],
      ['Vocabulary', practiceLinks.vocabulary],
      ['Flashcards', practiceLinks.flashcards],
      ...(language === 'fr' ? [['Extra Videos', videosPath]] : []),
    ];

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {[
          ['Speaking', practiceLinks],
          ['Listening', listeningLevelLinks],
        ].map(([label, links]) => (
          <React.Fragment key={label}>
            <PracticeGroupLabel sx={mobile ? { px: 0.5 } : undefined}>{label}</PracticeGroupLabel>
            <Box sx={{ display: 'flex', gap: 0.75, px: mobile ? 0.5 : 1, pb: 0.5 }}>
              {/* Levels stay visually identical — no active highlight — so none reads as preselected. */}
              {['a1', 'a2', 'b1'].map((level) => (
                <PracticeMenuLink key={level} to={links[level]} mobile={mobile} chip onClick={close}>
                  <span translate="no" className="notranslate">
                    {level.toUpperCase()}
                  </span>
                </PracticeMenuLink>
              ))}
            </Box>
          </React.Fragment>
        ))}
        <Divider sx={{ my: 0.75, mx: mobile ? 0.5 : 1.25 }} />
        <PracticeGroupLabel sx={mobile ? { px: 0.5 } : undefined}>By topic</PracticeGroupLabel>
        {topicLinks.map(([label, to]) => (
          <PracticeMenuLink key={label} to={to} mobile={mobile} active={isActivePath(to)} onClick={close}>
            {label}
          </PracticeMenuLink>
        ))}
      </Box>
    );
  };

  const mockExamGroups = [
    ['Spoken mock exam', oralMockExamPath],
    ['Listening mock exam', listeningMockExamPath],
  ];

  const MockExamLinkGroup = ({ mobile = false }) => {
    const close = mobile ? closeMobileMenu : closeMockExamMenu;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {mockExamGroups.map(([label, to]) => (
          <PracticeMenuLink key={label} to={to} mobile={mobile} active={isActivePath(to)} onClick={close}>
            {label}
          </PracticeMenuLink>
        ))}
      </Box>
    );
  };

  return (
    <>
      <HideOnScroll forceShow={mobileOpen}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            backdropFilter: 'blur(8px)',
            bgcolor: 'rgba(245,245,245,0.85)',
            boxShadow: '0 2px 12px 0 rgba(19,62,135,0.08)',
            color: 'text.primary',
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            {/* A clickable mark at top-left linking home is the single most
                conventional pattern on the web, and the logo was previously not
                rendered anywhere in the app. The wordmark carries the accessible
                name, so the image itself is decorative. */}
            <Box
              component={Link}
              to="/"
              aria-label="FIDE Prep — home"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': { textDecoration: 'none' },
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt=""
                aria-hidden
                width="28"
                height="28"
                sx={{ width: 28, height: 28, display: { xs: 'none', md: 'block' }, flexShrink: 0 }}
              />
              <Typography
                translate="no"
                className="notranslate"
                component="span"
                variant="h6"
                fontWeight={900}
                sx={{ letterSpacing: 1 }}
              >
                FIDE Prep
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            {/* Desktop Navigation */}
            <ScrollableNavContainer>
              <NavLink to={examOverviewPath}>Exam Structure</NavLink>
              {/* Practice + chevron read as a single control; the wrapper stays chromeless
                  like every other nav item (no hover fill) — the red underline lives on the
                  "Practice" label itself, same as before. */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                }}
              >
                <NavLink to={chaptersPath} activePaths={[chaptersPath, buildTopicsBasePath(language)]}>
                  Practice
                </NavLink>
                <IconButton
                  aria-label="Open practice links"
                  aria-controls={practiceMenuOpen ? 'practice-links-menu' : undefined}
                  aria-expanded={practiceMenuOpen ? 'true' : undefined}
                  aria-haspopup="menu"
                  onClick={(event) => setPracticeMenuAnchorEl(event.currentTarget)}
                  size="small"
                  disableRipple
                  sx={{
                    p: 0.25,
                    color: 'text.secondary',
                    bgcolor: 'transparent',
                    '&:hover': { bgcolor: 'transparent' },
                  }}
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: practiceMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 180ms',
                    }}
                  />
                </IconButton>
                <Menu
                  id="practice-links-menu"
                  anchorEl={practiceMenuAnchorEl}
                  open={practiceMenuOpen}
                  onClose={closePracticeMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{
                    paper: {
                      elevation: 0,
                      sx: {
                        mt: 1.25,
                        minWidth: 260,
                        py: 1,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'rgba(19,62,135,0.10)',
                        boxShadow: '0 12px 32px rgba(19,62,135,0.16)',
                        overflow: 'hidden',
                      },
                    },
                  }}
                  MenuListProps={{ 'aria-label': 'Practice links', sx: { py: 0 } }}
                >
                  <PracticeLinkGroups />
                </Menu>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                }}
              >
                <NavLink
                  to={oralMockExamPath}
                  activePaths={[buildMockExamsPath(language), oralMockExamPath, listeningMockExamPath]}
                >
                  Mock Exams
                </NavLink>
                <IconButton
                  aria-label="Open mock exam links"
                  aria-controls={mockExamMenuOpen ? 'mock-exam-links-menu' : undefined}
                  aria-expanded={mockExamMenuOpen ? 'true' : undefined}
                  aria-haspopup="menu"
                  onClick={(event) => setMockExamMenuAnchorEl(event.currentTarget)}
                  size="small"
                  disableRipple
                  sx={{
                    p: 0.25,
                    color: 'text.secondary',
                    bgcolor: 'transparent',
                    '&:hover': { bgcolor: 'transparent' },
                  }}
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      transform: mockExamMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 180ms',
                    }}
                  />
                </IconButton>
                <Menu
                  id="mock-exam-links-menu"
                  anchorEl={mockExamMenuAnchorEl}
                  open={mockExamMenuOpen}
                  onClose={closeMockExamMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{
                    paper: {
                      elevation: 0,
                      sx: {
                        mt: 1.25,
                        minWidth: 200,
                        py: 1,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'rgba(19,62,135,0.10)',
                        boxShadow: '0 12px 32px rgba(19,62,135,0.16)',
                        overflow: 'hidden',
                      },
                    },
                  }}
                  MenuListProps={{ 'aria-label': 'Mock exam links', sx: { py: 0 } }}
                >
                  <MockExamLinkGroup />
                </Menu>
              </Box>
              <NavLink to={examTipsPath}>Tips</NavLink>
              {SHOW_QA ? <NavLink to="/questions">Community</NavLink> : null}
              {SHOW_QA && isAdmin ? <NavLink to="/admin/qa/moderation">Moderation</NavLink> : null}
              <NavLink to="/pricing">Pricing</NavLink>
            </ScrollableNavContainer>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {SHOW_QA && isVerifiedUser ? <NotificationBell /> : null}
              <FormControl
                size="small"
                sx={{
                  minWidth: 0,
                  width: 'auto',
                  bgcolor: 'background.paper',
                  borderRadius: 1.5,
                  height: theme.spacing(5),
                }}
              >
                <Select
                  value={displayedLanguage}
                  onChange={handleLanguageChange}
                  inputProps={{ 'aria-label': 'Choose practice language' }}
                  displayEmpty
                  disabled={languageChangePending || languageLoading}
                  IconComponent={NoDropdownIcon}
                  renderValue={(selected) => {
                    const option = languageOptions[selected];
                    const isSaving = pendingLanguage === selected;
                    if (!option) {
                      return null;
                    }
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                        <Box
                          sx={{
                            position: 'relative',
                            width: 24,
                            height: 18,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <img
                            src={option.flag}
                            alt={option.ariaLabel}
                            style={{
                              width: '24px',
                              height: 'auto',
                              borderRadius: '2px',
                              objectFit: 'cover',
                              opacity: isSaving ? 0.35 : 1,
                            }}
                          />
                          {isSaving && <CircularProgress size={18} color="inherit" sx={{ position: 'absolute' }} />}
                        </Box>
                      </Box>
                    );
                  }}
                  sx={{
                    fontWeight: 700,
                    width: 'auto',
                    height: theme.spacing(5),
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      justifyContent: 'center',
                      paddingRight: `${theme.spacing(1)} !important`,
                      paddingLeft: theme.spacing(1),
                      paddingTop: theme.spacing(0.75),
                      paddingBottom: theme.spacing(0.75),
                    },
                  }}
                >
                  <MenuItem value="fr">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img
                        src={languageOptions.fr.flag}
                        alt={languageOptions.fr.ariaLabel}
                        style={{ width: '24px', height: 'auto', borderRadius: '2px', objectFit: 'cover' }}
                      />
                      {languageOptions.fr.label}
                    </Box>
                  </MenuItem>
                  <MenuItem value="de">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img
                        src={languageOptions.de.flag}
                        alt={languageOptions.de.ariaLabel}
                        style={{ width: '24px', height: 'auto', borderRadius: '2px', objectFit: 'cover' }}
                      />
                      {languageOptions.de.label}
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
              <Box
                sx={{
                  minWidth: authDecisionPending || isVerifiedUser ? 96 : { xs: 'auto', sm: 96 },
                  minHeight: theme.spacing(5),
                  display: authDecisionPending || isVerifiedUser ? { xs: 'none', sm: 'flex' } : 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {authDecisionPending ? (
                  <Box
                    data-testid="navbar-auth-loading"
                    sx={{
                      width: theme.spacing(5),
                      height: theme.spacing(5),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress size={20} color="inherit" />
                  </Box>
                ) : isVerifiedUser ? (
                  <Box
                    data-testid="navbar-auth-avatar"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                    onClick={handleProfileClick}
                  >
                    <Avatar
                      sx={{ bgcolor: 'success.main', width: 32, height: 32, display: { xs: 'none', sm: 'flex' } }}
                    >
                      <PersonIcon />
                    </Avatar>
                    {/* <Box>
                      <Typography fontWeight={700} sx={{ display: { xs: 'none', sm: 'block' } }}>
                        {user.displayName || user.email}
                      </Typography>
                    </Box> */}
                  </Box>
                ) : (
                  <Button
                    component={Link}
                    to="/auth/login"
                    state={buildAuthRedirectState(location)}
                    variant="contained"
                    onClick={() => {
                      setMobileOpen(false);
                    }}
                    color="secondary"
                    sx={{
                      fontWeight: 700,
                      borderRadius: 2,
                      px: 3,
                      boxShadow: 2,
                      display: 'flex',
                      mx: 0,
                    }}
                  >
                    Login
                  </Button>
                )}
              </Box>

              {/* Profile Popup */}
              <Popover
                id={idProfilePopup}
                open={openProfilePopup}
                anchorEl={anchorEl}
                onClose={handleProfileClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center', // Align center horizontally with the avatar
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center', // Align center horizontally with the popover
                }}
                PaperProps={{
                  sx: {
                    mt: 1.5, // Add a small margin top to spacing it from the avatar
                    borderRadius: 1,
                    boxShadow: 3,
                    px: 3,
                    py: 2,
                    minWidth: 200,
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {user && (
                    <>
                      <Typography
                        translate="no"
                        className="notranslate"
                        variant="body2"
                        color="text.secondary"
                        align="center"
                      >
                        {user.email}
                      </Typography>

                      <Button
                        component={Link}
                        to={accountPath}
                        onClick={handleProfileClose}
                        variant="text"
                        color="secondary"
                        size="small"
                        fullWidth
                        sx={{ fontWeight: 600, borderRadius: 2, textTransform: 'none', mx: 0 }}
                      >
                        Account
                      </Button>

                      <Button
                        onClick={() => {
                          handleProfileClose();
                          handleLogout();
                        }}
                        variant="outlined"
                        color="secondary"
                        sx={{ ml: 2, fontWeight: 700, borderRadius: 2, display: { xs: 'none', sm: 'flex' }, mx: 0 }}
                      >
                        Logout
                      </Button>
                    </>
                  )}
                </Box>
              </Popover>
              {/* Mobile menu button */}
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  ml: 'auto',
                }}
              >
                {mobileOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            </Box>
          </Toolbar>

          {/* Mobile Navigation */}
          <Collapse
            in={mobileOpen && isMobile}
            timeout="auto"
            unmountOnExit
            sx={{
              display: { xs: 'block', md: 'none' },
              bgcolor: 'rgba(245,245,245,0.95)',
              py: 1,
              px: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                py: 0.75,
              }}
            >
              <NavLink to={examOverviewPath} onClick={() => setMobileOpen(false)}>
                Exam Structure
              </NavLink>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <NavLink to={chaptersPath} onClick={closeMobileMenu} sx={{ flexGrow: 1 }}>
                  Practice
                </NavLink>
                {/* Labelled outline button: clearly tappable without competing with the nav links. */}
                <Button
                  aria-label="Toggle practice links"
                  aria-controls="mobile-practice-links"
                  aria-expanded={mobilePracticeOpen ? 'true' : 'false'}
                  onClick={() => setMobilePracticeOpen((open) => !open)}
                  size="small"
                  variant="outlined"
                  endIcon={
                    <ExpandMoreIcon
                      sx={{
                        transform: mobilePracticeOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 180ms',
                      }}
                    />
                  }
                  sx={{
                    mx: 0,
                    py: 0.25,
                    px: 1,
                    minWidth: 0,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                    borderColor: 'divider',
                    '& .MuiButton-endIcon': { ml: 0.25 },
                  }}
                >
                  More Practice
                </Button>
              </Box>
              <Collapse in={mobilePracticeOpen} timeout="auto" unmountOnExit id="mobile-practice-links">
                {/* Inset panel so the sub-links read as children of Practice, not siblings. */}
                <Box
                  sx={{
                    ml: 1,
                    px: 1,
                    py: 1,
                    borderRadius: 1,
                    bgcolor: 'rgba(19,62,135,0.04)',
                    borderLeft: '3px solid',
                    borderColor: 'rgba(19,62,135,0.25)',
                  }}
                >
                  <PracticeLinkGroups mobile />
                </Box>
              </Collapse>
              <NavLink
                to={oralMockExamPath}
                activePaths={[buildMockExamsPath(language), oralMockExamPath, listeningMockExamPath]}
                onClick={closeMobileMenu}
              >
                Mock Exams
              </NavLink>
              <NavLink to={examTipsPath} onClick={() => setMobileOpen(false)}>
                Exam Tips
              </NavLink>
              {SHOW_QA ? (
                <NavLink to="/questions" onClick={() => setMobileOpen(false)}>
                  Community
                </NavLink>
              ) : null}
              {SHOW_QA && isAdmin ? (
                <NavLink to="/admin/qa/moderation" onClick={() => setMobileOpen(false)}>
                  Moderation
                </NavLink>
              ) : null}
              <NavLink to="/pricing" onClick={() => setMobileOpen(false)}>
                Pricing
              </NavLink>
              <NavLink to="/contact-us" onClick={() => setMobileOpen(false)}>
                Contact Us
              </NavLink>
              {isVerifiedUser ? (
                <>
                  <Divider />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                      <PersonIcon />
                    </Avatar>
                    <Typography
                      translate="no"
                      className="notranslate"
                      variant="body2"
                      sx={{ flexGrow: 1, fontWeight: 600 }}
                    >
                      {user.email}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 2 }}>
                    <NavLink to={accountPath} onClick={() => setMobileOpen(false)} sx={{ fontWeight: 400 }}>
                      Account
                    </NavLink>
                  </Box>
                  <Button
                    fullWidth
                    onClick={() => {
                      handleLogout();
                      setMobileOpen(false);
                    }}
                    variant="contained"
                    color="secondary"
                    sx={{
                      mt: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1,
                      mx: 0,
                    }}
                  >
                    Logout
                  </Button>
                </>
              ) : (
                !authDecisionPending && (
                  <>
                    <Divider />
                    <Button
                      fullWidth
                      component={Link}
                      to="/auth/login"
                      state={buildAuthRedirectState(location)}
                      onClick={() => {
                        setMobileOpen(false);
                      }}
                      variant="contained"
                      color="secondary"
                      sx={{
                        mt: 1,
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1,
                        mx: 0,
                      }}
                    >
                      Login
                    </Button>
                    <Button
                      fullWidth
                      component={Link}
                      to="/auth/signup"
                      state={buildAuthRedirectState(location)}
                      onClick={() => {
                        setMobileOpen(false);
                      }}
                      variant="outlined"
                      color="secondary"
                      sx={{
                        mt: 1,
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1,
                        mx: 0,
                      }}
                    >
                      Signup
                    </Button>
                  </>
                )
              )}
              <Divider />
            </Box>
          </Collapse>
        </AppBar>
      </HideOnScroll>
    </>
  );
};

// Reusable NavLink component for consistent styling
/**
 * The navbar previously gave no indication of which page you were on. The
 * indicator is the logo's red — the same accent the section rules use — so the
 * colour reads as the site's structural mark rather than as a stray hue in the
 * logo. Decorative only: `aria-current` carries the meaning for assistive tech.
 */
const NavLink = ({ to, children, onClick, sx, activePaths = [to] }) => {
  const location = useLocation();
  const isActive = activePaths.some((path) => path && path !== '/' && location.pathname.startsWith(path));

  return (
    <Typography
      component={Link}
      to={to}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      sx={{
        color: 'inherit',
        textDecoration: 'none',
        fontWeight: 700,
        display: 'block',
        position: 'relative',
        py: { xs: 0.75, md: 1 },
        '&::after': {
          content: '""',
          display: { xs: 'none', md: 'block' },
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 2,
          height: 3,
          borderRadius: 99,
          bgcolor: ACCENT_RED,
          opacity: isActive ? 1 : 0,
          transform: isActive ? 'scaleX(1)' : 'scaleX(0.4)',
          transition: 'opacity .2s ease, transform .2s ease',
        },
        '&:hover::after': { opacity: 1, transform: 'scaleX(1)' },
        '@media (prefers-reduced-motion: reduce)': {
          '&::after': { transition: 'none' },
        },
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
};

export default Navbar;
